import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { canManageUsers } from '@jfh/intake-shared';
import type { TeamsBotStatus, TeamsBotTarget } from '@jfh/intake-shared';
import { api, type Me } from '../lib/api.js';
import { API_ORIGIN } from '../lib/deployment.js';
import { PageHeader, EmptyState } from '../components/Primitives.js';
import { useToast } from '../components/Toast.js';
import { Icon } from '../components/Icon.js';
import { ApprovalReminders } from '../components/ApprovalReminders.js';
import { StageApprovers } from '../components/StageApprovers.js';
import { EmailTemplates } from '../components/EmailTemplates.js';
import { TeamsAppSummary, TeamsAppTarget } from '../components/TeamsApp.js';
import { Connections } from './settings/Connections.js';
import { LookupLists } from './settings/LookupLists.js';
import { CopyRow, CountriesPanel } from './settings/panels.js';

const CALLBACK_URL = `${API_ORIGIN}/api/webhooks/power-automate`;

type Section = 'teams' | 'email' | 'countries' | 'connections' | 'lookups' | 'security';

interface ChannelCfg {
  flowUrl?: string;
  group?: string;
  /** JFH Forms Teams app target (used once the stage switches from the flow). */
  bot?: TeamsBotTarget;
  /** 'bot' = approval cards go through the Teams app. */
  mode?: 'flow' | 'bot';
}
type TeamsChannels = Record<string, ChannelCfg>;

const CHANNELS: { name: string; blurb: string }[] = [
  { name: 'Customer Service', blurb: 'Trade applications, and stage 1 of credit applications.' },
  { name: 'Accounts', blurb: 'Stage 2 of credit applications (the credit decision).' },
  { name: 'Supplier', blurb: 'Supplier account applications.' },
];

/** The sub-nav, grouped (F9 P5): what office staff change, then one-off set-up. */
const NAV: { group: string; items: { key: Section; label: string }[] }[] = [
  {
    group: 'Approvals and messages',
    items: [
      { key: 'teams', label: 'Teams & approvals' },
      { key: 'email', label: 'Applicant emails' },
      { key: 'countries', label: 'Countries' },
    ],
  },
  {
    group: 'System set-up',
    items: [
      { key: 'connections', label: 'Connections' },
      { key: 'lookups', label: 'Lookup lists' },
      { key: 'security', label: 'Security' },
    ],
  },
];
const SECTIONS = NAV.flatMap((g) => g.items.map((i) => i.key));
/** Old `?section=` links (audit log, bookmarks) still land somewhere sensible. */
const OLD_SECTIONS: Record<string, Section> = { iq: 'connections' };

export function Settings() {
  const toast = useToast();
  const [me, setMe] = useState<Me | null>(null);
  // ?section=email etc. opens a section directly (e.g. from the audit log).
  const [params] = useSearchParams();
  const [section, setSection] = useState<Section>(() => {
    const s = params.get('section') ?? '';
    return SECTIONS.includes(s as Section) ? (s as Section) : (OLD_SECTIONS[s] ?? 'teams');
  });
  const [channels, setChannels] = useState<TeamsChannels>({});
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState<string | null>(null);
  const [saved, setSaved] = useState<TeamsChannels>({});
  const [bot, setBot] = useState<TeamsBotStatus | null>(null);
  const [botError, setBotError] = useState<string | null>(null);

  useEffect(() => {
    api.me().then(setMe).catch(() => setMe(null));
    api
      .getSettings()
      .then((r) => {
        const ch = (r.settings.teams_channels as TeamsChannels) ?? {};
        setChannels(ch);
        setSaved(ch);
      })
      .catch(() => undefined)
      .finally(() => setLoaded(true));
    api
      .getTeamsBot()
      .then(setBot)
      .catch((e: unknown) => setBotError(e instanceof Error ? e.message : 'Could not reach the Teams app'));
  }, []);

  if (me && !canManageUsers(me.role)) {
    return (
      <div style={{ padding: 28 }}>
        <EmptyState icon="shield" title="Admins only" body="You don't have permission to view settings." />
      </div>
    );
  }

  const setFlow = (name: string, flowUrl: string) =>
    setChannels((prev) => ({ ...prev, [name]: { ...prev[name], flowUrl } }));
  const setBotTarget = (name: string, target: TeamsBotTarget | undefined) =>
    setChannels((prev) => ({
      ...prev,
      // No target → can't use the Teams app.
      [name]: { ...prev[name], bot: target, mode: target ? prev[name]?.mode : 'flow' },
    }));
  const setMode = (name: string, mode: 'flow' | 'bot') =>
    setChannels((prev) => ({ ...prev, [name]: { ...prev[name], mode } }));
  const botDirty = (name: string) => JSON.stringify(channels[name]?.bot) !== JSON.stringify(saved[name]?.bot);

  const testBot = async (name: string) => {
    setTesting(`bot:${name}`);
    try {
      const r = await api.testTeamsBot(name);
      if (r.delivered) toast({ title: `${name}: test card posted by the Teams app`, icon: 'check' });
      else toast({ title: `${name}: Teams app couldn't post`, body: r.message?.slice(0, 160), icon: 'shield' });
    } catch (e) {
      toast({ title: 'Test failed', body: (e as Error).message, icon: 'shield' });
    } finally {
      setTesting(null);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.putSetting('teams_channels', channels);
      setSaved(channels);
      toast({ title: 'Teams settings saved', icon: 'check' });
    } catch (e) {
      toast({ title: 'Save failed', body: (e as Error).message, icon: 'shield' });
    } finally {
      setSaving(false);
    }
  };

  const test = async (name: string) => {
    setTesting(name);
    try {
      const r = await api.testTeams(name);
      if (!r.configured) toast({ title: `${name}: no flow URL set`, icon: 'shield' });
      else if (r.delivered) toast({ title: `${name}: test card delivered`, icon: 'check' });
      else
        toast({
          title: `${name}: flow returned ${r.status ?? 'an error'}`,
          body: r.message ? r.message.slice(0, 160) : undefined,
          icon: 'shield',
        });
    } catch (e) {
      toast({ title: 'Test failed', body: (e as Error).message, icon: 'shield' });
    } finally {
      setTesting(null);
    }
  };

  return (
    <div style={{ padding: 28, maxWidth: 980 }} className="fade-in">
      <PageHeader title="Settings" subtitle="Approvals and messages, and the one-off system set-up." />

      <div style={{ display: 'flex', gap: 24, marginTop: 18 }}>
        {/* sub-nav */}
        <nav aria-label="Settings sections" style={{ width: 190, flex: 'none', display: 'flex', flexDirection: 'column', gap: 2 }}>
          {NAV.map((g, gi) => (
            <div key={g.group} style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: gi ? 14 : 0 }}>
              <div
                style={{
                  fontSize: 10.5,
                  fontWeight: 800,
                  letterSpacing: '.1em',
                  textTransform: 'uppercase',
                  color: 'var(--jfh-cactus)',
                  padding: '0 11px 4px',
                }}
              >
                {g.group}
              </div>
              {g.items.map((n) => (
                <button
                  key={n.key}
                  onClick={() => setSection(n.key)}
                  aria-current={section === n.key ? 'page' : undefined}
                  style={{
                    textAlign: 'left',
                    padding: '8px 11px',
                    borderRadius: 7,
                    border: 'none',
                    background: section === n.key ? 'var(--accent-soft)' : 'transparent',
                    color: section === n.key ? 'var(--accent-text)' : 'var(--text-2)',
                    fontSize: 13.5,
                    fontWeight: 600,
                  }}
                >
                  {n.label}
                </button>
              ))}
            </div>
          ))}
        </nav>

        <div style={{ flex: 1, minWidth: 0 }}>
          {section === 'teams' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {!loaded ? (
                <div style={{ color: 'var(--text-3)' }}>Loading…</div>
              ) : (
                <>
                  <TeamsAppSummary status={bot} error={botError} />
                  {CHANNELS.map((ch) => {
                    const cfg = channels[ch.name] ?? {};
                    const viaApp = cfg.mode === 'bot' && Boolean(cfg.bot);
                    const connected = viaApp || Boolean(cfg.flowUrl);
                    return (
                      <div key={ch.name} className="card" style={{ padding: 'var(--pad-card)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <Icon name="teams" size={18} />
                          <strong style={{ fontSize: 14.5 }}>{ch.name}</strong>
                          <span className={`badge ${connected ? 'approved' : 'neutral'}`}>
                            {viaApp ? 'Teams app' : connected ? 'Power Automate' : 'Not configured'}
                          </span>
                        </div>
                        <div className="field-help" style={{ marginTop: 4 }}>{ch.blurb}</div>
                        <label className="field-label" style={{ marginTop: 12 }}>
                          Power Automate flow URL
                        </label>
                        <input
                          className="input"
                          placeholder="https://prod-….logic.azure.com/workflows/…/triggers/manual/paths/invoke?…"
                          value={cfg.flowUrl ?? ''}
                          onChange={(e) => setFlow(ch.name, e.target.value)}
                        />
                        <div style={{ marginTop: 10 }}>
                          <button
                            className="btn sm"
                            disabled={!connected || testing === ch.name}
                            onClick={() => test(ch.name)}
                          >
                            <Icon name="send" size={13} />
                            {testing === ch.name ? 'Sending…' : 'Send test card'}
                          </button>
                        </div>
                        <TeamsAppTarget
                          status={bot}
                          value={cfg.bot}
                          onChange={(t) => setBotTarget(ch.name, t)}
                          onTest={() => void testBot(ch.name)}
                          testing={testing === `bot:${ch.name}`}
                          dirty={botDirty(ch.name)}
                          mode={cfg.mode === 'bot' ? 'bot' : 'flow'}
                          onModeChange={(m) => setMode(ch.name, m)}
                        />
                      </div>
                    );
                  })}
                  <div>
                    <button className="btn primary" onClick={save} disabled={saving}>
                      {saving ? 'Saving…' : 'Save Teams settings'}
                    </button>
                  </div>

                  <StageApprovers />
                  <ApprovalReminders />

                  <div className="field-help">
                    Power Automate flows send decisions back to an address in System set-up → Connections → Teams
                    approvals.
                  </div>
                </>
              )}
            </div>
          )}

          {section === 'security' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <Row label="Signing in" value="Only JFH staff signed in with Cloudflare Access or Teams can open the portal." ok />
              <Row label="Teams flow decisions" value="Decisions sent back by Power Automate must carry the shared secret." ok />
              <Row label="Wix website forms" value="Wix must send the secret set in Connections → Wix website forms." />
              <div className="card" style={{ padding: 'var(--pad-card)' }}>
                <div className="field-label">Where Power Automate sends decisions</div>
                <CopyRow text={CALLBACK_URL} />
              </div>
            </div>
          )}

          {section === 'email' && <EmailTemplates />}
          {section === 'countries' && <CountriesPanel />}
          {section === 'connections' && <Connections />}
          {section === 'lookups' && <LookupLists />}
        </div>
      </div>
    </div>
  );
}


function Row({ label, value, ok }: { label: string; value: string; ok?: boolean }) {
  return (
    <div className="card" style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 12 }}>
      <span className={`badge ${ok ? 'approved' : 'neutral'}`}>{ok ? 'On' : 'Check'}</span>
      <strong style={{ fontSize: 13.5, width: 200 }}>{label}</strong>
      <span style={{ color: 'var(--text-2)', fontSize: 13 }}>{value}</span>
    </div>
  );
}
